import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Formatters } from '../../core/util/formatters';
import { today } from '../../core/util/datetime';
import { MAX_NOTES_LENGTH, MAX_PARTY_ONLINE } from '../../core/validation/bookingValidator';
import { BookingField } from '../../core/validation/fieldError';
import { S } from '../../i18n/strings';
import { useBooking, type BookingController } from '../../state/useBooking';
import { AvailabilityPanel } from '../components/AvailabilityPanel';
import { TextArea, TextField } from '../components/Fields';
import { ErrorBanner } from '../components/StateContainer';
import { CalendarIcon, ClockIcon, UsersIcon } from '../components/icons';
import { fieldErrorMessage } from '../components/errorPresentation';

type Step = 'SLOT' | 'DETAILS' | 'REVIEW';

const STEPS: { key: Step; label: string }[] = [
  { key: 'SLOT', label: S.bookingStepSlot },
  { key: 'DETAILS', label: S.bookingStepDetails },
  { key: 'REVIEW', label: S.bookingStepReview },
];

/**
 * Flujo de reserva en tres pasos sobre un único borrador.
 *
 * Los pasos son estado interno y no rutas propias: así el borrador no se pasa por la URL ni se
 * pierde al retroceder, y volver atrás desde la confirmación no puede reabrir un formulario ya
 * enviado.
 */
export function BookingScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editingReservationId = new URLSearchParams(location.search).get('reserva');

  const booking = useBooking(id, editingReservationId);
  const [step, setStep] = useState<Step>('SLOT');

  useEffect(() => {
    if (!booking.event) return;
    switch (booking.event.kind) {
      case 'CONFIRMED':
        booking.discardDraft();
        navigate(
          `/confirmacion/${booking.event.reservation.id}?modificada=${booking.event.wasModification}`,
          { replace: true },
        );
        break;
      case 'SLOT_TAKEN':
        // Reintentar no devuelve la mesa: el usuario vuelve al primer paso a elegir otra.
        setStep('SLOT');
        break;
      case 'REQUIRES_LOGIN':
        // El borrador queda guardado; al volver del login se retoma en el mismo punto.
        navigate(`/entrar?volver=${encodeURIComponent(location.pathname + location.search)}`);
        booking.clearEvent();
        break;
      default:
        break;
    }
  }, [booking, navigate, location]);

  return (
    <div className="stack">
      <Stepper current={step} />

      {booking.restaurant && (
        <div className="row">
          <strong>{booking.restaurant.name}</strong>
          {editingReservationId && <span className="chip chip--static">{S.bookingEditing}</span>}
        </div>
      )}

      {booking.event?.kind === 'SLOT_TAKEN' && (
        <ErrorBanner error={{ kind: 'SLOT_TAKEN' }} />
      )}
      {booking.event?.kind === 'FAILED' && (
        <ErrorBanner error={booking.event.error} onRetry={() => booking.submit()} />
      )}

      {step === 'SLOT' && (
        <SlotStep booking={booking} onContinue={() => setStep('DETAILS')} onBack={() => navigate(-1)} />
      )}
      {step === 'DETAILS' && (
        <DetailsStep booking={booking} onContinue={() => setStep('REVIEW')} onBack={() => setStep('SLOT')} />
      )}
      {step === 'REVIEW' && <ReviewStep booking={booking} onBack={() => setStep('DETAILS')} />}
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const currentIndex = STEPS.findIndex((s) => s.key === current);
  return (
    <nav className="stepper" aria-label="Progreso de la reserva">
      {STEPS.map((step, index) => (
        <span key={step.key} style={{ display: 'contents' }}>
          {index > 0 && (
            <span className="stepper__sep" aria-hidden="true">
              ›
            </span>
          )}
          <span
            className={`stepper__step ${
              index === currentIndex ? 'stepper__step--current' : index < currentIndex ? 'stepper__step--done' : ''
            }`}
            aria-current={index === currentIndex ? 'step' : undefined}
          >
            <span className="stepper__index">
              <span>{index + 1}</span>
            </span>
            {step.label}
          </span>
        </span>
      ))}
    </nav>
  );
}

function SlotStep({
  booking,
  onContinue,
  onBack,
}: {
  booking: BookingController;
  onContinue: () => void;
  onBack: () => void;
}) {
  const { draft, errors } = booking;

  return (
    <div className="stack">
      <div className="field__row">
        <div className="field">
          <label className="field__label" htmlFor="booking-date">
            <CalendarIcon size={14} /> {S.pickerDate}
          </label>
          <input
            id="booking-date"
            className="field__control"
            type="date"
            value={draft.date}
            min={today()}
            aria-invalid={!!errors[BookingField.DATE]}
            onChange={(event) => event.target.value && booking.setDate(event.target.value)}
          />
          {errors[BookingField.DATE] && (
            <p className="field__error">{fieldErrorMessage(errors[BookingField.DATE]!)}</p>
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="booking-time">
            <ClockIcon size={14} /> {S.pickerTime}
          </label>
          <input
            id="booking-time"
            className="field__control"
            type="time"
            step={1800}
            value={draft.time}
            aria-invalid={!!errors[BookingField.TIME]}
            onChange={(event) => event.target.value && booking.setTime(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="booking-party">
            <UsersIcon size={14} /> {S.pickerParty}
          </label>
          <select
            id="booking-party"
            className="field__control"
            value={draft.partySize}
            aria-invalid={!!errors[BookingField.PARTY_SIZE]}
            onChange={(event) => booking.setPartySize(Number(event.target.value))}
          >
            {Array.from({ length: MAX_PARTY_ONLINE }, (_, i) => i + 1).map((size) => (
              <option key={size} value={size}>
                {Formatters.partySize(size)}
              </option>
            ))}
          </select>
          {errors[BookingField.PARTY_SIZE] && (
            <p className="field__error">{fieldErrorMessage(errors[BookingField.PARTY_SIZE]!)}</p>
          )}
        </div>
      </div>

      <section className="section">
        <h2>{S.availabilityTitle}</h2>
        <AvailabilityPanel
          state={booking.availability}
          selectedToken={draft.selectedSlot?.token ?? null}
          onSelectSlot={booking.selectSlot}
          onSelectAlternative={(alternative) => {
            booking.setDate(alternative.date);
            booking.setTime(alternative.time);
            booking.setPartySize(alternative.partySize);
          }}
          onRetry={booking.reloadAvailability}
        />
        {errors[BookingField.TIME] && (
          <p className="field__error">
            {draft.selectedSlot ? fieldErrorMessage(errors[BookingField.TIME]!) : S.validationSlotRequired}
          </p>
        )}
      </section>

      <div className="row">
        <button type="button" className="btn btn--text" onClick={onBack}>
          {S.back}
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="btn"
          onClick={() => {
            if (booking.validateSlotStep()) onContinue();
          }}
        >
          {S.bookingContinue}
        </button>
      </div>
    </div>
  );
}

function DetailsStep({
  booking,
  onContinue,
  onBack,
}: {
  booking: BookingController;
  onContinue: () => void;
  onBack: () => void;
}) {
  const { draft, errors } = booking;

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (booking.validateGuestStep()) onContinue();
      }}
    >
      <TextField
        id="guest-name"
        label={S.bookingName}
        value={draft.name}
        onChange={booking.setName}
        onBlur={() => booking.validateField(BookingField.NAME)}
        error={errors[BookingField.NAME]}
        autoComplete="name"
      />
      <TextField
        id="guest-phone"
        label={S.bookingPhone}
        value={draft.phone}
        onChange={booking.setPhone}
        onBlur={() => booking.validateField(BookingField.PHONE)}
        error={errors[BookingField.PHONE]}
        type="tel"
        autoComplete="tel"
        placeholder="55 1234 5678"
      />
      <TextField
        id="guest-email"
        label={S.bookingEmail}
        value={draft.email}
        onChange={booking.setEmail}
        onBlur={() => booking.validateField(BookingField.EMAIL)}
        error={errors[BookingField.EMAIL]}
        type="email"
        autoComplete="email"
      />
      <TextField
        id="guest-occasion"
        label={S.bookingOccasion}
        value={draft.occasion ?? ''}
        onChange={(value) => booking.setOccasion(value || null)}
      />
      <TextArea
        id="guest-notes"
        label={S.bookingNotes}
        value={draft.notes}
        onChange={booking.setNotes}
        error={errors[BookingField.NOTES]}
        maxLength={MAX_NOTES_LENGTH}
        hint={S.bookingNotesCounter(draft.notes.length)}
      />

      <div className="row">
        <button type="button" className="btn btn--text" onClick={onBack}>
          {S.back}
        </button>
        <span className="spacer" />
        <button type="submit" className="btn">
          {S.bookingContinue}
        </button>
      </div>
    </form>
  );
}

function ReviewStep({ booking, onBack }: { booking: BookingController; onBack: () => void }) {
  const { draft, errors, restaurant } = booking;

  return (
    <div className="stack">
      <section className="section">
        <h2>{S.bookingSummary}</h2>
        <div className="card" style={{ padding: 16 }}>
          <dl className="summary">
            {restaurant && (
              <>
                <dt className="summary__key">Restaurante</dt>
                <dd className="summary__value">{restaurant.name}</dd>
              </>
            )}
            <dt className="summary__key">{S.pickerDate}</dt>
            <dd className="summary__value">{Formatters.longDate(draft.date)}</dd>
            <dt className="summary__key">{S.pickerTime}</dt>
            <dd className="summary__value">
              {Formatters.time(draft.selectedSlot?.time ?? draft.time)}
            </dd>
            <dt className="summary__key">{S.pickerParty}</dt>
            <dd className="summary__value">{Formatters.partySize(draft.partySize)}</dd>
            <dt className="summary__key">{S.reservationGuest}</dt>
            <dd className="summary__value">{draft.name}</dd>
            <dt className="summary__key">{S.bookingPhone}</dt>
            <dd className="summary__value">{draft.phone}</dd>
            <dt className="summary__key">{S.bookingEmail}</dt>
            <dd className="summary__value">{draft.email}</dd>
            {/* Los campos opcionales solo aparecen si el usuario los llenó. */}
            {draft.occasion && (
              <>
                <dt className="summary__key">{S.reservationOccasion}</dt>
                <dd className="summary__value">{draft.occasion}</dd>
              </>
            )}
            {draft.notes.trim() && (
              <>
                <dt className="summary__key">{S.reservationNotes}</dt>
                <dd className="summary__value">{draft.notes}</dd>
              </>
            )}
          </dl>
        </div>
      </section>

      <p className="muted">{S.bookingTermsBody}</p>

      <div className="field">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.termsAccepted}
            aria-invalid={!!errors[BookingField.TERMS]}
            onChange={(event) => booking.setTermsAccepted(event.target.checked)}
          />
          <span>{S.bookingTerms}</span>
        </label>
        {errors[BookingField.TERMS] && (
          <p className="field__error">{fieldErrorMessage(errors[BookingField.TERMS]!)}</p>
        )}
      </div>

      <div className="row">
        <button type="button" className="btn btn--text" onClick={onBack} disabled={booking.isSubmitting}>
          {S.back}
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="btn"
          onClick={() => booking.submit()}
          disabled={booking.isSubmitting}
        >
          {booking.isSubmitting ? S.loading : S.bookingConfirm}
        </button>
      </div>
    </div>
  );
}
